import React, { useEffect, useRef, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Select } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import moment from 'moment'
import WidgetWarehouseSelector from 'src/widgets/Selectors/WidgetWarehouseSelector'
import BaseListing from 'src/BaseListings'
import { useDownloadExcel } from 'react-export-table-to-excel'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import * as XLSX from 'xlsx'

const normalizeWarehouseLocationIds = (value) => {
  if (Array.isArray(value)) {
    return value.map((id) => +id).filter((id) => id > 0)
  }

  if (value === undefined || value === null || value === '') {
    return []
  }

  return String(value)
    .split(',')
    .map((id) => +id)
    .filter((id) => id > 0)
}

const StockReport = (props) => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [dateRange, setDateRange] = useState(props.date || moment().format('YYYY-MM-DD'))
  const [stockData, setStockData] = useState([])
  const [totalCost, setTotalCost] = useState(0)
  const [warehouses, setWarehouses] = useState([])
  const [WarehouseLocations, setWarehouseLocations] = useState([])
  const [Items, setItems] = useState([])
  const [WarehouseName, setWarehouseName] = useState('')
  const [WarehouseId, setWarehouseId] = useState(0)
  const [ItemName, setItemName] = useState('')
  const [ItemId, setItemId] = useState(0)
  const Base = new BaseListing('stock_report')
  const tableRef = useRef(null)
  const initialWarehouseLocationIds = []
  const [WarehouseLocationIds, setWarehouseLocationIds] = useState(initialWarehouseLocationIds)

  const companyName = wtGetFromLocalStorage('company_name')

  const changeValues = (key, val) => {
    if (key === 'warehouse_id') {
      setWarehouseId(val.value)
      setWarehouseName(val.label)
    }
    if (key === 'item_id') {
      setItemId(val.value)
      setItemName(val.label)
    }
    if (key === 'warehouse_location_ids') {
      setWarehouseLocationIds(val || [])
    }
  }

  const applyHiddenFilters = () => {
    const ref = props.report_ref
    if (!ref) {
      return false
    }

    const storedFilters = wtGetFromLocalStorage(`stock_report_filters_${ref}`, null)
    if (!storedFilters) {
      return false
    }

    if (storedFilters.date) {
      setDateRange(storedFilters.date)
    }
    if (storedFilters.warehouse_id !== undefined) {
      setWarehouseId(+storedFilters.warehouse_id || 0)
    }
    if (storedFilters.item_id !== undefined) {
      setItemId(+storedFilters.item_id || 0)
    }
    if (storedFilters.warehouse_location_ids !== undefined) {
      setWarehouseLocationIds(normalizeWarehouseLocationIds(storedFilters.warehouse_location_ids))
    }

    return {
      date: storedFilters.date || dateRange,
      warehouse_id: +storedFilters.warehouse_id || 0,
      item_id: +storedFilters.item_id || 0,
      warehouse_location_ids: normalizeWarehouseLocationIds(
        storedFilters.warehouse_location_ids,
      ).join(','),
    }
  }

  const print = () => {
    wtApiPostWithContentTypeHandling('reports/stock_report/print', {
      date: dateRange,
      warehouse_location_ids: WarehouseLocationIds.join(','),
      item: ItemId,
      warehouse_id: WarehouseId,
    })
  }

  const columns = [
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      key: 'branch',
    },
    {
      title: 'Warehouse',
      dataIndex: ['warehouse', 'name'],
      key: 'warehouse',
    },
    {
      title: 'Item Code',
      dataIndex: ['item', 'code'],
      key: 'item_code',
    },
    {
      title: 'Item Name',
      dataIndex: ['item', 'name'],
      key: 'item_name',
    },
    {
      title: 'Brand',
      dataIndex: ['item_brand', 'name'],
      key: 'brand_name',
    },
    {
      title: 'Unit',
      dataIndex: ['item_unit', 'name'],
      key: 'unit_name',
    },
    {
      title: 'Quantity',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      align: 'right',
      render: (value) => wtRoundValue(value, 0),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_value',
      key: 'total_value',
      align: 'right',
      render: (value) => wtFormatMoney(value, '', true, 0),
    },
  ]

  const getReportData = (overrideFilters = null) => {
    const filters = overrideFilters || {
      date: dateRange,
      warehouse_id: WarehouseId,
      item_id: ItemId,
      warehouse_location_ids: WarehouseLocationIds.join(','),
    }
    setIsLoading(true)
    wtApiCall(
      'reports/stock_report',
      {
        date: filters.date,
        warehouse_id: filters.warehouse_id,
        item_id: filters.item_id,
        warehouse_location_ids: filters.warehouse_location_ids,
      },
      'get',
      (response) => {
        if (response && response.data) {
          setStockData(response.data.data || [])
          setTotalCost(wtFormatMoney(response.data.totalCost, 'PKR', true, 0))
        } else {
          setStockData([])
          setTotalCost(0)
        }
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }
  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: 'Stock Report',
    sheet: 'Sheet',
  })

  const exportWithSerials = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/stock_report',
      {
        date: dateRange,
        warehouse_id: WarehouseId,
        item_id: ItemId,
        warehouse_location_ids: WarehouseLocationIds.join(','),
        include_serials: 1,
      },
      'get',
      (response) => {
        const report = response?.data || {}
        const summaryRows = stockData.map((row) => ({
          Branch: row.branch?.name || '',
          Warehouse: row.warehouse?.name || '',
          'Item Code': row.item?.code || '',
          'Item Name': row.item?.name || '',
          Brand: row.item_brand?.name || '',
          Unit: row.item_unit?.name || '',
          Quantity: wtRoundValue(row.total_quantity, 0),
          'Total Cost': wtFormatMoney(row.total_value, '', true, 0),
        }))
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(summaryRows),
          'Stock Report',
        )
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(report.serial_data || []),
          'Item Serials',
        )
        XLSX.writeFile(workbook, 'Stock Report With Serial.xlsx')
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }
  useEffect(() => {
    const hiddenFilters = applyHiddenFilters()
    wtApiCall(
      'dropdown',
      {
        dropdown_names: 'warehouses,items,warehouse_locations_with_permissions,warehouse_locations',
      },
      'get',
      (result) => {
        const warehousesData = result?.data?.[0]?.warehouses || []
        const itemsData = result?.data?.[1]?.items || []
        const warehouseLocationsWithPermissions =
          result?.data?.[2]?.warehouse_locations_with_permissions || []
        const warehouseLocations = result?.data?.[3]?.warehouse_locations || []
        const resolvedWarehouseLocations =
          warehouseLocationsWithPermissions.length > 0
            ? warehouseLocationsWithPermissions
            : warehouseLocations

        setWarehouses(warehousesData)
        setItems(itemsData)
        setWarehouseLocations(resolvedWarehouseLocations)

        const defaultWarehouseLocationIds = resolvedWarehouseLocations
          .map((item) => +item.value || +item.id || 0)
          .filter((item) => item > 0)

        if (!hiddenFilters) {
          setWarehouseLocationIds(defaultWarehouseLocationIds)
        }

        getReportData(
          hiddenFilters || {
            date: dateRange,
            warehouse_id: WarehouseId,
            item_id: ItemId,
            warehouse_location_ids: defaultWarehouseLocationIds.join(','),
          },
        )
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        getReportData(
          hiddenFilters || {
            date: dateRange,
            warehouse_id: WarehouseId,
            item_id: ItemId,
            warehouse_location_ids: WarehouseLocationIds.join(','),
          },
        )
      },
    )
  }, [])

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Card
        title={
          <span style={{ fontSize: '22px', marginLeft: '40%' }}>
            {companyName + ' Stock Report'}
          </span>
        }
        style={{ width: '100%' }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={12} sm={8} md={5}>
            <label>As Of: *</label>
            <WidgetDatePicker value={dateRange} setDate={setDateRange} />
          </Col>

          <Col xs={12} sm={8} md={5}>
            <div className="form-group">
              <label>Warehouse *</label>
              <WidgetWarehouseSelector
                warehouses={warehouses}
                onChange={(values) => changeValues('warehouse_id', values)}
                displayValue={WarehouseName}
                reloadDropdownData={() => Base.dropdownData('warehouses', {}, [setWarehouses], 1)}
                value={WarehouseId}
              />
            </div>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <div className="form-group">
              <label>Item</label>
              <WidgetItemSelector
                items={Items}
                onChange={(values) => changeValues('item_id', values)}
                displayValue={ItemName}
                reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
                placeHolder={'Select Item'}
                value={ItemId}
              />
            </div>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <div className="form-group">
              <label>Warehouse Location </label>
              <Select
                mode="multiple"
                maxTagCount="responsive"
                options={WarehouseLocations}
                value={WarehouseLocationIds}
                onChange={(values) => changeValues('warehouse_location_ids', values)}
                placeholder="Select Location"
                style={{ width: '100%' }}
              />
            </div>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Button
              style={{ marginTop: 30 }}
              type="primary"
              onClick={() => getReportData()}
              disabled={isLoading}
            >
              Generate
            </Button>
            <Button
              className="text-white"
              style={{
                marginBottom: '20px',
                marginTop: '20px',
                marginLeft: '1%',
                backgroundColor: 'green',
              }}
              onClick={onDownload}
            >
              Export
            </Button>
            {Base.hasRights('Export Stock Report With Serial') && (
              <Button
                className="text-white"
                style={{
                  marginBottom: '20px',
                  marginTop: '20px',
                  marginLeft: '1%',
                  backgroundColor: '#1677ff',
                }}
                onClick={exportWithSerials}
                disabled={isLoading}
              >
                Export With Serial
              </Button>
            )}
            <Button style={{  backgroundColor: "red", color: "#fff" }} onClick={print} disabled={isLoading ? true : false}>Print</Button>

          </Col>
        </Row>

        <br />
        <div style={{ width: '100%' }}>
          <table className="table table-bordered data-table" ref={tableRef}>
            <Table
              columns={columns}
              dataSource={stockData}
              pagination={false}
              bordered
              size="small"
              rowKey={(record, index) => index}
              loading={{ indicator: <Spin />, spinning: isLoading }}
              footer={() => (
                <div style={{ textAlign: 'right' }}>
                  {/* <h6 style={{ marginBottom: 0 }}>Total Inventory Value: {totalCost}</h6> */}
                </div>
              )}
            />
          </table>
        </div>
      </Card>
    </div>
  )
}

export default StockReport
